import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from '../../../shared/components/toast/services/toast.service';
import { EnumRunControlState } from '../../enums/run-control-state.enum';
import { ModalService } from '../../../shared/components/modal/services/modal.service';
import { EnumAttachmentUploadState } from '../../enums/attachment-upload-state.enum';
import { IAttachmentUpload } from '../../interfaces/attachment-upload.interface';
import { IConversationFile } from '../../interfaces/conversation-file.interface';
import {
  IReusableAttachment,
  isReusableAttachment,
  isUserAttachment
} from '../../interfaces/message-attachment.interface';
import {
  ComposerDraftStore,
  NEW_CONVERSATION_DRAFT
} from '../../services/composer-draft.store';
import { MessageAttachmentsStore } from '../../services/message-attachments.store';
import { ComposerDataStore } from '../../services/composer-data.store';
import { attachmentTypeLabel, formatFileSize } from '../../utils/attachment.utils';
import { attachmentIcon } from '../../utils/attachment-icon.util';
import { ComposerMenuComponent } from '../composer-menu/composer-menu.component';
import { FilePickerModalComponent } from '../file-picker-modal/file-picker-modal.component';
import { IChatInputMessage } from '../../interfaces/chat-input.interface';
import { IKnowledgeDocument } from '../../interfaces/knowledge-document.interface';
import { IQuickAction } from '../../interfaces/quick-action.interface';
import {
  documentLabel,
  knowledgeDocumentIcon
} from '../../utils/knowledge-document-icon.util';
import {
  adjustHeight,
  clearEditable,
  getTextContent,
  placeCursorAtEnd,
  readMentionQuery,
  removeTag,
  replaceMentionWithTag,
  updateTagSelection
} from '../../utils/content-editable.utils';
import { KnowledgeCacheService } from '../../services/knowledge-cache.service';
import { KnowledgeMenuComponent } from '../knowledge-menu/knowledge-menu.component';
import { QuickActionsMenuComponent } from '../quick-actions-menu/quick-actions-menu.component';


@Component({
  selector: 'sgt-chat-input',
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss'],
  providers: [ComposerDataStore],
})
export class ChatInputComponent implements OnDestroy {
  @Input() placeholder = 'Pergunte alguma coisa ou peça uma ação...';
  @Input() sending = false;
  @Input() runControl: EnumRunControlState = EnumRunControlState.IDLE;
  @Input() conversationFiles: IConversationFile[] = [];

  /** Cada conversa tem o seu rascunho: trocar de conversa troca o que está escrito. */
  @Input() set sessionId(value: string) {
    const alvo = value || NEW_CONVERSATION_DRAFT;
    if (alvo === this.currentSessionId) {
      return;
    }
    this.persist();
    // O arquivo não sobrevive à saída da conversa: só o que foi escrito volta.
    this.drafts.discardUploads(this.currentSessionId);
    this.currentSessionId = alvo;
    this.restore();
  }
  get sessionId(): string {
    return this.currentSessionId;
  }
  @Output() messageSent = new EventEmitter<IChatInputMessage>();
  @Output() stopRequested = new EventEmitter<void>();

  @ViewChild('inputField') inputField: ElementRef<HTMLDivElement>;
  @ViewChild(ComposerMenuComponent) composerMenu?: ComposerMenuComponent;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @ViewChild(QuickActionsMenuComponent) quickActionsMenu: QuickActionsMenuComponent;
  @ViewChild(KnowledgeMenuComponent) knowledgeMenu: KnowledgeMenuComponent;

  messageText = '';
  attachments: IAttachmentUpload[] = [];
  isDraggingFile = false;

  readonly attachmentIcon = attachmentIcon;
  quickActionTagSelected = false;
  showQuickActionsMenu = false;
  quickActionsMenuQuery = '';
  activeQuickAction: IQuickAction | null = null;

  documentTagSelected = false;
  showKnowledgeMenu = false;
  pickedFiles: IConversationFile[] = [];
  knowledgeMenuQuery = '';
  activeDocument: IKnowledgeDocument | null = null;

  readonly documents$ = this.data.documents$;
  readonly documentsLoading$ = this.data.documentsLoading$;
  readonly documentsTruncated$ = this.data.documentsTruncated$;
  readonly quickActions$ = this.data.quickActions$;

  private destroy$ = new Subject<void>();
  private currentSessionId = NEW_CONVERSATION_DRAFT;
  private uploadsSubscription: Subscription | null = null;

  constructor(
    private data: ComposerDataStore,
    private knowledgeCache: KnowledgeCacheService,
    private drafts: ComposerDraftStore,
    private toastService: ToastService,
    private elementRef: ElementRef,
    private modalService: ModalService,
  ) {
    // O store esvazia a lista na invalidação; ao componente cabe fechar o menu.
    this.knowledgeCache.invalidated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.showKnowledgeMenu = false));

    this.watchUploads();
  }

  private get uploads(): MessageAttachmentsStore {
    return this.drafts.get(this.currentSessionId).uploads;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // O alvo pode já ter saído do DOM: trocar de painel destrói o botão clicado
    // antes deste handler rodar, e aí `contains` diria que veio de fora.
    const target = event.target as Node;
    if (!target.isConnected || this.elementRef.nativeElement.contains(target)) {
      return;
    }
    this.showQuickActionsMenu = false;
    this.showKnowledgeMenu = false;
    this.composerMenu?.close();
  }

  /** O escolhido na conversa já está pronto: entra na lista como upload concluído. */
  get visibleAttachments(): IAttachmentUpload[] {
    const reaproveitados = this.pickedFiles
      .map((file) => file.attachment)
      .filter(isReusableAttachment)
      .map((attachment) => ({
        key: attachment.attachmentId,
        name: attachment.name,
        mime: attachment.mime,
        sizeBytes: isUserAttachment(attachment) ? attachment.sizeBytes : 0,
        state: EnumAttachmentUploadState.READY,
        progress: 100,
        attachment,
        errorMessage: null,
        errorRetryable: false
      }));

    return [...this.attachments, ...reaproveitados];
  }

  get hasConversationFiles(): boolean {
    return this.conversationFiles.length > 0;
  }

  onActionsRequested(): void {
    this.quickActionsMenuQuery = '';
    this.openQuickActionsMenu();
  }

  onActionsBack(): void {
    this.showQuickActionsMenu = false;
    this.composerMenu?.backToRoot();
  }

  /** O menu de ações vive dentro do popover: fechar um sem o outro trava o `+`. */
  closeActions(): void {
    this.showQuickActionsMenu = false;
    this.composerMenu?.close();
  }

  /** O `/` abre as ações por fora do popover; o botão reassume o controle. */
  onComposerMenuOpened(): void {
    this.showQuickActionsMenu = false;
  }

  onComposerMenuClosed(): void {
    this.showQuickActionsMenu = false;
  }

  onPickFromConversation(): void {
    const modalRef = this.modalService.open(FilePickerModalComponent);
    modalRef.instance.files = this.conversationFiles;
    modalRef.instance.filesPicked
      .pipe(takeUntil(this.destroy$))
      .subscribe((files: IConversationFile[]) => (this.pickedFiles = files));
  }

  removePickedFile(key: string): void {
    this.pickedFiles = this.pickedFiles.filter(
      (item) => item.attachment.attachmentId !== key
    );
  }

  onInput(): void {
    const el = this.inputField?.nativeElement;
    if (!el) {
 return;
}

    this.messageText = getTextContent(el);
    if (this.quickActionTagSelected) {
      this.quickActionTagSelected = false;
      updateTagSelection(el, 'quickAction', false);
    }
    if (this.documentTagSelected) {
      this.documentTagSelected = false;
      updateTagSelection(el, 'document', false);
    }
    this.onInputChange(this.messageText);
    adjustHeight(el);

    if (!this.messageText.trim() && !this.activeQuickAction && !this.activeDocument) {
      el.innerHTML = '';
    }
    this.persist();
  }

  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length && this.canAttach) {
      event.preventDefault();
      this.addFiles(files);
      return;
    }
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    document.execCommand('insertText', false, text);
  }

  onQuickActionSelected(action: IQuickAction): void {
    this.activeQuickAction = action;
    this.messageText = '';
    this.closeActions();
    const el = this.inputField?.nativeElement;
    if (el) {
      el.innerHTML = '';
      placeCursorAtEnd(el);
      adjustHeight(el);
    }
    this.persist();
  }

  onDocumentSelected(document: IKnowledgeDocument): void {
    this.activeDocument = document;
    this.showKnowledgeMenu = false;
    const el = this.inputField?.nativeElement;
    if (el) {
      const { className, modifier } = knowledgeDocumentIcon(document.detectedMime);
      replaceMentionWithTag(
        el,
        'document',
        documentLabel(document),
        `${className} chat-input__document-tag-icon--${modifier}`
      );
      this.messageText = getTextContent(el);
      adjustHeight(el);
    }
    this.persist();
  }

  onSend(): void {
    if (this.sending || !this.canSend) {
      return;
    }
    this.sendWithAttachments();
  }

  openFilePicker(): void {
    if (this.canAttach) {
      this.fileInput?.nativeElement.click();
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    // Permite reanexar o mesmo arquivo, que de outro modo não dispara `change`.
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    if (!this.canAttach) {
      return;
    }
    event.preventDefault();
    this.isDraggingFile = true;
  }

  onDragLeave(): void {
    this.isDraggingFile = false;
  }

  onDrop(event: DragEvent): void {
    this.isDraggingFile = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (!files.length || !this.canAttach) {
      return;
    }
    event.preventDefault();
    this.addFiles(files);
  }

  removeAttachment(item: IAttachmentUpload): void {
    this.uploads.remove(item.key);
    this.removePickedFile(item.key);
  }

  trackByAttachmentKey(_: number, item: IAttachmentUpload): string {
    return item.key;
  }

  attachmentSize(item: IAttachmentUpload): string {
    return formatFileSize(item.sizeBytes);
  }

  isUploading(item: IAttachmentUpload): boolean {
    return item.state === EnumAttachmentUploadState.UPLOADING;
  }

  isFailed(item: IAttachmentUpload): boolean {
    return item.state === EnumAttachmentUploadState.FAILED;
  }

  attachmentStatusLabel(item: IAttachmentUpload): string {
    if (item.state === EnumAttachmentUploadState.FAILED) {
      return item.errorMessage ?? 'Não foi possível anexar o arquivo.';
    }
    if (item.state === EnumAttachmentUploadState.PROCESSING) {
      return 'Processando…';
    }
    if (item.state === EnumAttachmentUploadState.UPLOADING) {
      return `Enviando… ${item.progress}%`;
    }
    // O artefato da AyA não informa o tamanho: melhor o tipo que um "0 B".
    return item.attachment && !isUserAttachment(item.attachment)
      ? attachmentTypeLabel(item.mime, item.name)
      : formatFileSize(item.sizeBytes);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.handleEscape(event)) {
 return;
}
    if (this.handleBackspaceQuickAction(event)) {
 return;
}
    if (this.handleBackspaceDocument(event)) {
 return;
}
    if (this.handleQuickActionTagDeselect(event)) {
 return;
}
    if (this.handleDocumentTagDeselect(event)) {
 return;
}
    if (this.handleMenuNavigation(event)) {
 return;
}
    this.handleEnter(event);
  }

  // Abrir pelo botão digita o `@` por baixo, mantendo um único fluxo de menção.
  toggleKnowledgeMenu(): void {
    if (this.activeQuickAction || this.activeDocument || this.hasAttachments) {
      return;
    }
    if (this.showKnowledgeMenu) {
      this.showKnowledgeMenu = false;
      return;
    }

    const el = this.inputField?.nativeElement;
    if (el) {
      placeCursorAtEnd(el);
      document.execCommand('insertText', false, this.needsSpaceBeforeMention() ? ' @' : '@');
      this.onInput();
    }
  }

  removeActiveDocument(): void {
    this.activeDocument = null;
    this.documentTagSelected = false;
    const el = this.inputField?.nativeElement;
    if (el) {
      removeTag(el, 'document');
      this.messageText = getTextContent(el);
      adjustHeight(el);
      placeCursorAtEnd(el);
    }
  }

  removeActiveQuickAction(): void {
    this.activeQuickAction = null;
    const el = this.inputField?.nativeElement;
    if (el) {
      adjustHeight(el);
      placeCursorAtEnd(el);
    }
  }

  discardAttachments(): void {
    this.uploads.discard();
  }

  /**
   * O card é mais alto que o campo: o espaço reservado à barra de ações fica
   * entre os dois e não é clicável por si. O clique ali leva o cursor ao texto.
   */
  onWrapperClick(event: MouseEvent): void {
    const alvo = event.target as HTMLElement;
    if (alvo.closest('button, a, input, [contenteditable="true"]')) {
      return;
    }
    this.focus();
  }

  focus(): void {
    const el = this.inputField?.nativeElement;
    if (el) {
      placeCursorAtEnd(el);
    }
  }

  ngOnDestroy(): void {
    this.persist();
    // Sair da tela também é sair da conversa: o arquivo não fica esperando.
    this.drafts.discardUploads(this.currentSessionId);
    this.destroy$.next();
    this.destroy$.complete();
    this.uploadsSubscription?.unsubscribe();
  }

  /** O escolhido na conversa já está no servidor: entra por referência, sem subir de novo. */
  private sendWithAttachments(): void {
    const reaproveitados = this.pickedFiles
      .map((file) => file.attachment)
      .filter(isReusableAttachment);

    this.emitMessage([...this.uploads.attachments, ...reaproveitados]);
  }

  private emitMessage(attachments: IReusableAttachment[] = []): void {
    const text = this.messageText.trim();
    const el = this.inputField?.nativeElement;

    this.messageSent.emit({
      text,
      selectedKnowledgeDocument: this.activeDocument
        ? {
            id: this.activeDocument.id,
            title: this.activeDocument.title,
            originalFilename: this.activeDocument.originalFilename,
            detectedMime: this.activeDocument.detectedMime
          }
        : undefined,
      quickActionId: this.activeQuickAction?.id,
      quickActionTitle: this.activeQuickAction?.title,
      attachments: attachments.length ? attachments : undefined
    });
    this.messageText = '';
    this.activeQuickAction = null;
    this.activeDocument = null;
    this.pickedFiles = [];
    this.drafts.clear(this.currentSessionId);
    if (el) {
      clearEditable(el);
    }
  }

  private persist(): void {
    this.drafts.save(this.currentSessionId, {
      html: this.inputField?.nativeElement.innerHTML ?? '',
      text: this.messageText,
      quickAction: this.activeQuickAction,
      document: this.activeDocument
    });
  }

  /** As tags do campo são DOM: só o HTML salvo as devolve inteiras. */
  private restore(): void {
    const draft = this.drafts.get(this.currentSessionId);
    this.messageText = draft.text;
    this.activeQuickAction = draft.quickAction;
    this.activeDocument = draft.document;
    const el = this.inputField?.nativeElement;
    if (el) {
      el.innerHTML = draft.html;
      adjustHeight(el);
    }
    this.watchUploads();
  }

  private watchUploads(): void {
    this.uploadsSubscription?.unsubscribe();
    this.attachments = this.uploads.items;
    this.uploadsSubscription = this.uploads
      .getItems$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => (this.attachments = items));
  }

  /** Enquanto a resposta corre, o mesmo lugar oferece parar em vez de enviar. */
  get canStop(): boolean {
    return this.runControl !== EnumRunControlState.IDLE;
  }

  get isStopping(): boolean {
    return this.runControl === EnumRunControlState.STOPPING;
  }

  get canSend(): boolean {
    const text = this.messageText.trim();
    if (!this.uploads.isEmpty) {
      // O anexo precisa estar pronto: a mensagem viaja com o id, não com o arquivo.
      return !this.uploads.hasPendingUpload;
    }
    if (this.activeDocument) {
      return !!text;
    }
    // O arquivo escolhido na conversa já está pronto: não há upload a esperar.
    return !!text || !!this.activeQuickAction || !!this.pickedFiles.length;
  }

  get hasAttachments(): boolean {
    return !this.uploads.isEmpty;
  }

  get canAttach(): boolean {
    return (
      !this.activeDocument &&
      !this.activeQuickAction &&
      this.remainingSlots > 0
    );
  }

  /** As duas origens disputam o mesmo teto de cinco anexos por mensagem. */
  private get remainingSlots(): number {
    return this.uploads.remainingSlots - this.pickedFiles.length;
  }

  get attachTooltip(): string {
    if (this.activeDocument || this.activeQuickAction) {
      return 'Anexos não podem ser combinados com documento ou habilidade';
    }
    return this.remainingSlots > 0
      ? 'Anexar arquivos'
      : 'Limite de 5 arquivos por mensagem';
  }

  private addFiles(files: File[]): void {
    if (!files.length || !this.canAttach) {
      return;
    }
    this.uploads.add(files).forEach((rejection) =>
      this.toastService.error(`${rejection.name}: ${rejection.reason}`)
    );
  }

  private handleEscape(event: KeyboardEvent): boolean {
    if (event.key !== 'Escape' || (!this.showQuickActionsMenu && !this.showKnowledgeMenu)) {
      return false;
    }
    this.closeActions();
    this.showKnowledgeMenu = false;
    return true;
  }

  private handleBackspaceQuickAction(event: KeyboardEvent): boolean {
    if (event.key !== 'Backspace' || this.messageText || !this.activeQuickAction) {
      return false;
  }
    event.preventDefault();
    if (this.quickActionTagSelected) {
      this.quickActionTagSelected = false;
      this.removeActiveQuickAction();
    } else {
      this.quickActionTagSelected = true;
      const el = this.inputField?.nativeElement;
      if (el) {
 updateTagSelection(el, 'quickAction', true);
}
    }
    return true;
  }

  private handleBackspaceDocument(event: KeyboardEvent): boolean {
    if (event.key !== 'Backspace' || this.messageText || !this.activeDocument) {
 return false;
}
    event.preventDefault();
    if (this.documentTagSelected) {
      this.documentTagSelected = false;
      this.removeActiveDocument();
    } else {
      this.documentTagSelected = true;
      const el = this.inputField?.nativeElement;
      if (el) {
 updateTagSelection(el, 'document', true);
}
    }
    return true;
  }

  private handleDocumentTagDeselect(event: KeyboardEvent): boolean {
    if (this.documentTagSelected && event.key !== 'Backspace') {
      this.documentTagSelected = false;
      const el = this.inputField?.nativeElement;
      if (el) {
 updateTagSelection(el, 'document', false);
}
    }
    return false;
  }

  private handleQuickActionTagDeselect(event: KeyboardEvent): boolean {
    if (this.quickActionTagSelected && event.key !== 'Backspace') {
      this.quickActionTagSelected = false;
      const el = this.inputField?.nativeElement;
      if (el) {
 updateTagSelection(el, 'quickAction', false);
}
    }
    return false;
  }

  /**
   * Quando o menu de ações rápidas (`/`) ou o de documentos (`@`) está visível,
   * captura as teclas de navegação antes que o `contenteditable` do
   * input tente processá-las:
   * - ArrowDown/ArrowUp: move o destaque entre os itens do menu.
   * - Enter (sem Shift): seleciona o item destacado e fecha o menu se
   *   não houver seleção válida.
   * - Shift+Enter: ignorado (cai no fluxo normal — quebra de linha).
   *
   * Sem o `event.preventDefault()` antes de delegar ao menu, as setas
   * moveriam o cursor de texto e o Enter inseriria uma quebra de linha
   * no input, em vez de navegar pelo menu.
   *
   * Retorno `true` indica que o evento foi consumido — `onKeydown` usa
   * esse retorno para parar a sequência de handlers.
   */
  private handleMenuNavigation(event: KeyboardEvent): boolean {
    const menu = this.openMenu;
    if (!menu) {
      return false;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        menu.moveHighlight(1);
        return true;

      case 'ArrowUp':
        event.preventDefault();
        menu.moveHighlight(-1);
        return true;

      case 'Enter':
        if (event.shiftKey) {
          return false;
        }
        event.preventDefault();
        if (!menu.selectHighlighted()) {
          this.closeActions();
          this.showKnowledgeMenu = false;
        }
        return true;

      default:
        return false;
    }
  }

  private get openMenu(): QuickActionsMenuComponent | KnowledgeMenuComponent | null {
    if (this.showQuickActionsMenu && this.quickActionsMenu) {
      return this.quickActionsMenu;
    }
    if (this.showKnowledgeMenu && this.knowledgeMenu) {
      return this.knowledgeMenu;
    }
    return null;
  }

  private handleEnter(event: KeyboardEvent): boolean {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
      return true;
    }
    return false;
  }

  private onInputChange(value: string): void {
    if (!this.activeDocument && value.startsWith('/')) {
      this.showKnowledgeMenu = false;
      this.composerMenu?.close();
      this.quickActionsMenuQuery = value.slice(1);
      this.openQuickActionsMenu();
      return;
    }
    this.closeActions();
    this.syncKnowledgeMenu();
  }

  private needsSpaceBeforeMention(): boolean {
    const texto = this.messageText;
    return !!texto && !!texto.slice(-1).trim();
  }

  private syncKnowledgeMenu(): void {
    const el = this.inputField?.nativeElement;
    const query = el && !this.activeQuickAction ? readMentionQuery(el) : null;

    if (query === null) {
      this.showKnowledgeMenu = false;
      return;
    }

    // Com um documento já escolhido o menu abre só para explicar o limite,
    // em vez de ignorar o `@` como se nada tivesse acontecido.
    if (this.activeDocument) {
      this.showKnowledgeMenu = true;
      return;
    }

    this.knowledgeMenuQuery = query;
    this.openKnowledgeMenu();
  }

  private openQuickActionsMenu(): void {
    this.showQuickActionsMenu = true;
    if (!this.data.hasQuickActions) {
      this.data.loadQuickActions();
    }
  }

  // Uma carga por abertura: digitar refiltra o que já está em memória.
  private openKnowledgeMenu(): void {
    this.composerMenu?.close();
    const jaAberto = this.showKnowledgeMenu;
    this.showKnowledgeMenu = true;
    if (jaAberto) {
      return;
    }
    this.data.loadDocuments();
  }
}
